<template>
  <div class="issuance-doc">
    <div class="issuance-doc__header">
      <span
        class="text-uppercase text-secondary fw-bold fs-10 letter-spacing-eyebrow"
      >
        Issuance ops · {{ getFlagEmoji(country) }} {{ countryName }}
      </span>
      <button
        type="button"
        class="btn-close"
        aria-label="Close country notes"
        @click="$emit('close')"
      ></button>
    </div>
    <template v-if="doc">
      <dl v-if="doc.facts.length" class="issuance-doc__facts">
        <template v-for="fact in doc.facts" :key="fact.label">
          <dt class="text-secondary">{{ fact.label }}</dt>
          <dd>
            <a
              v-if="fact.href"
              :href="fact.href"
              target="_blank"
              rel="noopener noreferrer"
              >{{ fact.value }}</a
            >
            <template v-else>{{ fact.value }}</template>
          </dd>
        </template>
      </dl>
      <!-- eslint-disable-next-line vue/no-v-html — repo-authored markdown, see issuanceDocs.ts -->
      <div class="issuance-doc__body" v-html="doc.html"></div>
    </template>
    <div v-else-if="loading" class="issuance-doc__placeholder text-secondary">
      Loading notes…
    </div>
    <div
      v-else-if="loadFailed"
      class="issuance-doc__placeholder text-secondary"
    >
      Couldn't load the notes for {{ countryName }}.
      <button type="button" class="btn btn-link btn-sm p-0" @click="loadDoc">
        Retry
      </button>
    </div>
    <div v-else class="issuance-doc__placeholder text-secondary">
      No issuance notes for {{ countryName }} yet — contact Patryk for more
      info.
    </div>
  </div>
</template>

<script setup lang="ts">
import { issuanceDoc, IssuanceDoc } from "@/admin/issuance/issuanceDocs";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  country: string;
}>();

defineEmits<{
  close: [];
}>();

const { t, te } = useI18n();

const { globalErrorHandler } = useGlobalHandlers();

const doc = ref<IssuanceDoc | null>(null);
const loading = ref(false);
const loadFailed = ref(false);

const countryName = computed(() => translateCountryCode(props.country, t, te));

// Identity per load so a stale request can't overwrite a newer one, toast a
// stale failure, or clear `loading` early — a country check alone can't tell
// two loads of the same country apart (LT → XX → LT).
let loadId = 0;

const loadDoc = async (): Promise<void> => {
  const id = ++loadId;
  doc.value = null;
  loading.value = true;
  loadFailed.value = false;
  try {
    const loaded = await issuanceDoc(props.country);
    if (id === loadId) doc.value = loaded;
  } catch (error) {
    if (id === loadId) {
      loadFailed.value = true;
      globalErrorHandler(error);
    }
  } finally {
    if (id === loadId) loading.value = false;
  }
};

watch(() => props.country, loadDoc, { immediate: true });
</script>

<style lang="scss" scoped>
.issuance-doc {
  background: #fff;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.issuance-doc__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.letter-spacing-eyebrow {
  letter-spacing: 0.08em;
}

.issuance-doc__facts {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 1rem;
  row-gap: 0.125rem;
  margin: 0 0 0.75rem;
  padding: 0.625rem 0.75rem;
  background: var(--phoenix-gray-100);
  border-radius: 0.375rem;
  font-size: 0.8125rem;

  dt {
    font-weight: 500;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
}

.issuance-doc__placeholder {
  padding: 1.5rem 0;
  text-align: center;
  font-size: 0.875rem;
}

// Minimal notion-like typography for the rendered markdown. :deep() because the
// content comes from v-html, outside the scoped-style hash.
.issuance-doc__body {
  overflow-y: auto;
  max-height: 70vh;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--phoenix-gray-900);

  :deep(h1) {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }

  :deep(h2) {
    font-size: 1rem;
    margin: 1.25rem 0 0.5rem;

    &:first-child {
      margin-top: 0;
    }
  }

  :deep(h3) {
    font-size: 0.875rem;
    margin: 1rem 0 0.375rem;
  }

  :deep(p),
  :deep(ul),
  :deep(ol) {
    margin-bottom: 0.625rem;
  }

  :deep(blockquote) {
    border-left: 3px solid var(--phoenix-gray-300);
    padding-left: 0.75rem;
    color: var(--phoenix-gray-700);
    margin: 0 0 0.625rem;
  }

  :deep(code) {
    font-size: 0.8125rem;
    background: var(--phoenix-gray-100);
    border-radius: 0.25rem;
    padding: 0.0625rem 0.25rem;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 0.75rem;
    font-size: 0.8125rem;
  }

  :deep(th),
  :deep(td) {
    border: 1px solid var(--phoenix-border-color);
    padding: 0.25rem 0.5rem;
    text-align: left;
    vertical-align: top;
  }

  :deep(th) {
    background: var(--phoenix-gray-100);
  }

  :deep(hr) {
    margin: 1rem 0;
  }
}
</style>
